<template>
  <ValidationObserver ref="validator">
    <form ref="form" @submit.prevent="onSubmit">
      <div
        class="row px-5"
        :class="{ 'join-corporate': showCorporates }"
        v-on:keydown.enter.prevent
      >
        <div :class="[showTransliteration ? 'col-md-6' : 'col-12 pt-md-5 pt-0 pb-2']">
          <Title id="personal-information" :text="translate('chPersonalInfo')" />
        </div>
        <div v-if="showTransliteration" class="col-12 col-md-6 d-none d-md-block">
          <Title id="personal-information" :text="translate('chPersonalInfoTrans')" />
          <div class="tooltip-container">
            <i class="icon-info-b2b xs"></i>
            <span class="tooltip-text">
              {{ translate('chPersonalInfoTooltip') }}
            </span>
          </div>
        </div>
        <Transliteration
          id="firstname"
          name="customer.firstName"
          :label="translate('rwFirstName')"
          :rules="{ regex: FormRegex.INPUT_TEXT, required: true }"
          data-testid="firstname"
          :transRules="{
            regex: FormRegex.INPUT_TRANSLITERATE_CHAR_EXCEPTION,
            required: true
          }"
          :transLabel="showTransliteration ? translate('chNameUserLang') : translate('chName')"
          :classes="!showTransliteration ? '' : 'col-12 col-md-6'"
          :hidden="!showTransliteration"
        />
        <div v-if="showTransliteration" class="col-12 d-sm-block d-md-none">
          <Title id="personal-information" :text="translate('chPersonalInfoTrans')" />
          <div class="tooltip-container">
            <i class="icon-info-b2b xs"></i>
            <span class="tooltip-text">
              {{ translate('chPersonalInfoTooltip') }}
            </span>
          </div>
        </div>
        <Transliteration
          id="lastname"
          name="customer.lastName"
          :label="translate('rwLastName')"
          :rules="{ regex: FormRegex.INPUT_TEXT, required: true }"
          data-testid="lastname"
          :transRules="{
            regex: FormRegex.INPUT_TRANSLITERATE_CHAR_EXCEPTION,
            required: true
          }"
          :transLabel="
            showTransliteration ? translate('chLastNameUserLang') : translate('chLastName')
          "
          :classes="!showTransliteration ? '' : 'col-12 col-md-6'"
          :hidden="!showTransliteration"
        />
        <div class="col-12 col-md-6">
          <SelectField
            id="country"
            name="customer.countryCode"
            :placeholder="translate('rwCountryOfResidence')"
            :options="countries"
            rules="required"
            data-testid="country"
            v-on:changed="onChanged"
            :autocomplete="true"
          />
        </div>
        <div class="col-12 col-md-6">
          <SelectField
            id="lang"
            name="customer.language"
            :placeholder="translate('rwLanguage')"
            :options="languages"
            rules="required"
            data-testid="language"
            :autocomplete="true"
          />
        </div>
        <div v-if="!showCorporates" class="col-12 col-md-6">
          <TextField
            id="email"
            name="customer.email"
            :label="translate('rwEmail')"
            type="email"
            :rules="{
              required: true,
              email: true,
              regex: '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}'
            }"
            data-testid="email"
          />
          <div v-if="isOcicoUpsellConsentFF" class="text-12">
            {{ translate('rwEmailOCIUpsellDisclaimer') }}
          </div>
        </div>
        <div class="col-12 col-md-6">
          <Phone
            name="customer.telephone"
            :placeholder="`${translate('rwPhone')} (${translate('rwOptional')})`"
            data-testid="telephone"
            :defaultCountry="defaultCountry"
            disableFlags
            id="field-tel"
            :inputOptions="inputOptions"
            :enabledCountryCode="true"
            :maxLen="30"
          />
          <label v-if="isOcicoUpsellConsentFF" class="text-12">{{
            translate('rwWhatsAppOCIDisclaimer')
          }}</label>
        </div>
        <template v-if="showCorporates">
          <div class="col-12 pt-md-5"></div>
          <div class="col-12 col-md-6">
            <TextField
              id="corporateEmail"
              name="customer.email"
              :label="translate('rwEmail')"
              type="email"
              :value="corporateEmail"
              :disabled="true"
              :rules="{
                required: true,
                email: true,
                regex: '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}'
              }"
              data-testid="corporateEmail"
            />
            <div v-if="isOcicoUpsellConsentFF" class="text-12">
              {{ translate('rwEmailOCIUpsellDisclaimer') }}
            </div>
          </div>
          <div class="col-12 col-md-6">
            <TextField
              id="corporateID"
              name="customer.corporateId"
              :label="translate('rwCorporateId')"
              :value="corporateID"
              :disabled="true"
              data-testid="corporateID"
            />
          </div>
        </template>
        <div class="col-12 pt-4 pb-2"><Title :text="translate('rwPassword')" /></div>
        <div class="col-12">
          <PasswordField id="password" name="customer.password" />
        </div>
        <div v-if="isFriendlyCaptchaEnable" class="col-12 pt-3 pt-md-0 w-100">
          <FriendlyRecaptcha
            location="signup"
            @widget-completed="onFriendlyCaptchaCompleted"
            @widget-error="onFriendlyCaptchaError"
            @widget-expire="onFriendlyCaptchaExpire"
            @widget-reset="onFriendlyCaptchaReset"
            @reset-action="onSetResetAction"
          />
        </div>
        <div v-if="showPromoCode" class="col-12 col-md-6 py-4">
          <div class="pb-2"><Title :text="translate('rwPromoCode')" /></div>
          <TextField
            id="promotionalCode"
            name="customer.promotionalCode"
            :label="translate('rwYourCode')"
            :value="promoCode"
            :disabled="true"
            :hideIcon="true"
            data-testid="promotionalCode"
          />
          <span class="promo-code-message">{{ translate('rwPromoCodeApplied') }}</span>
        </div>

        <DynamicCheckboxes :checkboxes="this.checkboxes" cssClass="col-12 dynamic-checkbox" />
        <div class="col-12 pt-5">
          <button
            v-if="cssLoaded"
            class="btn btn-primary text-uppercase join-submit-btn"
            :class="loading ? 'm-progress' : ''"
            :disabled="!isSubmitEnabled"
            type="submit"
          >
            {{ translate('rwJoinNowRadissonRewards') }}
          </button>
        </div>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import SelectField from '@/components/form-controls/SelectField'
import TextField from '@/components/form-controls/TextField'
import Title from '@/components/form-controls/Title'
import Phone from '@/components/form-controls/Phone'
import DynamicCheckboxes from '@/components/form-controls/DynamicCheckboxes'
import PasswordField from '@/components/form-controls/PasswordField'
import { contentService, loyaltyService } from '@/infrastructure/Factory'
import formJson from 'form-json'
import {
  getQueryParams,
  getPageLanguage,
  showModalRewardsLoginOnEmailAlreadyUsedError,
  getPiplCheckboxLabel
} from '@/infrastructure/Utils'
import { mapState, mapActions } from 'vuex'
import {
  FormRegex,
  OcicoUpsellEmailConsents,
  OcicoWhatsAppConsent,
  consentPiplEnabled
} from '@/infrastructure/Configuration'
import Transliteration from '@/components/form-controls/Transliteration.vue'
import showTransliteration from '@/components/mixins/showTransliteration'
import getUrlsParams from '@/components/mixins/getUrlParams'
import FriendlyCaptchaMixin from '@/components/mixins/FriendlyRecaptcha'
import FriendlyRecaptcha from '../components/form-controls/FriendlyRecaptcha.vue'
import { getFormErrorValues } from '@/infrastructure/TrackingFormErrors'

export default {
  name: 'SignUpB2C',
  components: {
    PasswordField,
    ValidationObserver,
    Title,
    TextField,
    SelectField,
    Phone,
    DynamicCheckboxes,
    Transliteration,
    FriendlyRecaptcha
  },
  mixins: [showTransliteration, getUrlsParams, FriendlyCaptchaMixin],
  data: () => ({
    languages: [],
    countries: [],
    checkboxes: [],
    loading: false,
    defaultCountry: '',
    promoCode: '',
    memberRefererCode: '',
    FormRegex,
    employeeCode: '',
    success: false,
    inputOptions: {
      showDialCode: false
    },
    corporateEmail: '',
    corporateID: '',
    isOcicoUpsellConsentFF: global.ConfigDynamic.isEnabled(
      global.ConfigDynamicKey.BOOKING_CICO_UPSELLING_CONSENT
    ),
    cssLoaded: false
  }),
  props: {
    isChina: Boolean
  },
  methods: {
    ...mapActions(['trackEvent', 'trackErrorEventEnrollment']),
    consentPiplEnabled,
    async onSubmit(event) {
      event.preventDefault()
      const isOk = await this.$refs.validator.validate()
      if (isOk) {
        this.loading = true
        let json = formJson(event.target)
        json = this.isOcicoUpsellConsentFF ? this.addEmailOcicoUpsellConsents(json) : json

        if (this.isFriendlyCaptchaEnable) {
          json.cToken = this.captchaToken
        }

        try {
          let jsonToSend = this.setJsonWithQueryParams(json)

          if (jsonToSend.customer) {
            jsonToSend = global.UtilsText.parseTextFromStringObject(jsonToSend)
          }

          await loyaltyService.signupPassword(jsonToSend)
          const showB2cDataGathering = '?displayDataGathering=true'
          this.trackEvent({
            eventName: 'FORM_SUBMISSION',
            eventData: {
              formName: this.getAnalyticsFormName
            }
          })
          this.success = true
          global.AuthDispatcher.login(
            null,
            `/${getPageLanguage().toLowerCase()}/radisson-rewards/secure/my-account${showB2cDataGathering}`
          )
        } catch (error) {
          const err = Object.assign({}, error.response.data, { status: error.response.status })
          this.trackEvent({
            eventName: 'FORM_ERROR',
            eventData: {
              formErrorValue: err?.title || getFormErrorValues(error),
              formName: this.getAnalyticsFormName
            }
          })

          if (error.response && error.response.status === 409) {
            showModalRewardsLoginOnEmailAlreadyUsedError(json.customer.email)
          } else if (error.response && error.response.status === 400) {
            global.ApiHandlers.handleErrorsMessage(err)
          } else {
            global.ApiHandlers.handleErrorsMessage(err)
          }
          this.loading = false
        } finally {
          this.onResetCaptcha()
        }
      } else {
        this.trackErrorEventEnrollment({
          error: this.$refs.validator.errors,
          type: 'rewards_join_b2c_form'
        })
        document.getElementById('personal-information').scrollIntoView({ behavior: 'smooth' })
      }
    },
    onChanged(country) {
      this.defaultCountry = country
      const hasCnRequirementCheckbox = this.checkboxes.some(
        checkbox => checkbox.id === 'checkboxPIPL'
      )

      const isChinaPiplEnabled = this.consentPiplEnabled(country)

      if (isChinaPiplEnabled && !hasCnRequirementCheckbox) {
        this.checkboxes.push({
          label: this.lblBoCheckboxPIPL,
          rules: 'checked',
          name: 'customer.consentPIPL',
          id: 'checkboxPIPL',
          checked: false,
          dataTestid: 'checkboxPIPL'
        })
      } else {
        this.checkboxes = this.checkboxes.filter(checkbox => checkbox.id !== 'checkboxPIPL')
      }
    },
    getPromoCode() {
      const { partnerCode } = getQueryParams(window.location.search)
      this.promoCode = partnerCode
    },
    getCorporateInfo() {
      const { corporateInfo } = global.appGlobalProps
      this.corporateEmail = corporateInfo?.corporateEmail
      this.corporateID = corporateInfo?.corporateID
    },
    getRefererCode() {
      const { refererCode } = getQueryParams(window.location.search)
      this.memberRefererCode = refererCode
    },
    getEmployeeCode() {
      const { employeeCode } = getQueryParams(window.location.search)
      this.employeeCode = employeeCode?.slice(0, 10)
    },
    getJsonWithReferer(json) {
      const jsonWithRefererCode = { ...json }
      jsonWithRefererCode.customer.memberReferer = this.memberRefererCode
      return jsonWithRefererCode
    },
    getJsonWithEmployeeCode(json) {
      const jsonWithEmployeeCode = { ...json }
      jsonWithEmployeeCode.customer.employeeId = this.employeeCode
      return jsonWithEmployeeCode
    },
    getJsonWithOriginProperty(json) {
      const jsonWithOriginProperty = { ...json }
      jsonWithOriginProperty.customer.originPropertyId = this.originPropertyId
      return jsonWithOriginProperty
    },
    setJsonWithQueryParams(json) {
      let jsonToSend
      jsonToSend = this.memberRefererCode ? this.getJsonWithReferer(json) : json
      jsonToSend = this.originPropertyId ? this.getJsonWithOriginProperty(json) : json
      jsonToSend = this.employeeCode ? this.getJsonWithEmployeeCode(json) : json
      return jsonToSend
    },
    addWhatsAppOcicoConsents(json) {
      OcicoWhatsAppConsent.forEach(consentType => {
        json.customer = { ...json.customer, [consentType]: json.telephone !== '' }
      })

      return json
    },
    addEmailOcicoUpsellConsents(json) {
      OcicoUpsellEmailConsents.forEach(consentType => {
        json.customer = { ...json.customer, [consentType]: true }
      })
      json = this.addWhatsAppOcicoConsents(json)

      return json
    }
  },
  computed: {
    ...mapState(['showTransliteratedField']),
    termsAndConditions() {
      return `<button role="button" aria-label="link-terms-conditions" class="btn btn-link btn-link--inline" data-target="#modal-default" data-href="/${getPageLanguage().toLowerCase()}/system/include/modal/rewards-terms-conditions">${this.translate(
        this.isChina ? 'rwTermsOfServiceLink' : 'rwTermsAndConditions'
      )}</button>`
    },
    linkPrivacyPolicy() {
      return `<button role="button" aria-label="link-privacy-policy" class="btn btn-link btn-link--inline" data-target="#modal-default" data-href="/${getPageLanguage().toLowerCase()}/system/include/modal/privacy-policy#loyalty-programs">${this.translate(
        this.isChina ? 'rwPrivacyPolicyLink' : 'rwPrivacyPolicy'
      )}</button>`
    },
    linkPrivacyNotice() {
      return `<button role="button" aria-label="link-privacy-policy" class="btn btn-link btn-link--inline" data-target="#modal-default" data-href="/${getPageLanguage().toLowerCase()}/system/include/modal/privacy-policy#loyalty-programs">${this.translate(
        'peCheckboxPIPLPrivacyNotice'
      )}</button>`
    },
    linkMarketingEmailConsent() {
      return `<button role="button" aria-label="link-privacy-policy" class="btn btn-link btn-link--inline" data-target="#modal-default" data-href="/${getPageLanguage().toLowerCase()}/system/include/modal/privacy-policy#subscription-to-our-newsletters">${this.translate(
        this.isChina ? 'rwPrivacyPolicyLink' : 'rwPrivacyPolicy'
      )}</button>`
    },
    crossTransferLinkChina() {
      return `<button role="button" aria-label="link-privacy-policy" class="btn btn-link btn-link--inline" data-target="#modal-default" data-href="/${getPageLanguage().toLowerCase()}/system/include/modal/privacy-policy#loyalty-programs">${this.translate(
        'rwAgreeWithPrivacyPolicyLink'
      )}</button>`
    },
    lblTermsAndConditions() {
      return this.translate('rwAgreeTermsAndConditions', undefined, this.termsAndConditions)
    },
    lblBoCheckboxPIPL() {
      return getPiplCheckboxLabel(this.translate, this.linkPrivacyNotice)
    },
    lblPrivacyPolicy() {
      return this.translate('bmmdAgreePrivacyPolicy', undefined, this.linkPrivacyPolicy)
    },
    lblMarketingEmailsConsent() {
      return this.translate(
        'rwAgreeReceiveCommunications',
        undefined,
        this.linkMarketingEmailConsent
      )
    },
    lblTermsAndPolicyChina() {
      return this.translate(
        'rwAgreeTermsAndConditionsChina',
        undefined,
        this.termsAndConditions,
        this.linkPrivacyPolicy
      )
    },
    lblCrossTransferChina() {
      return this.translate(
        'rwIAgreeTransferInformationChina',
        undefined,
        this.crossTransferLinkChina
      )
    },
    showPromoCode() {
      return this.promoCode !== '' && this.promoCode !== undefined
    },
    showCorporates() {
      return !!this.corporateID && !!this.corporateEmail
    },
    getAnalyticsFormName() {
      return this.showCorporates ? 'rewards_join_corporate_form' : 'rewards_join_b2c_form'
    },
    isFriendlyCaptchaEnable() {
      if (this.showCorporates) {
        return global.ConfigDynamic.isEnabled({ key: 'enable.web.friendly.captcha.corporate' })
      }

      if (this.showPromoCode) {
        return global.ConfigDynamic.isEnabled({ key: 'enable.web.friendly.captcha.partners' })
      }

      return global.ConfigDynamic.isEnabled(global.ConfigDynamicKey.WEB_FRIENDLY_CAPTCHA_B2C)
    },
    isSubmitEnabled() {
      return this.isFriendlyCaptchaEnable ? this.isCaptchaCompleted : true
    }
  },
  async created() {
    this.defaultCountry = this.$store.getters.userCountry
    this.$store.dispatch('sendAnalyticsView')
  },
  async mounted() {
    addEventListener('beforeunload', () => {
      if (!this.success) {
        this.trackEvent({
          eventName: 'FORM_ABANDONMENT',
          eventData: {
            formName: this.getAnalyticsFormName
          }
        })
      }
    })
    this.checkboxes = [
      {
        label: !this.isChina ? this.lblTermsAndConditions : this.lblTermsAndPolicyChina,
        rules: 'checked',
        name: 'customer.termsAndConditions',
        id: 'terms-conditions',
        checked: false,
        dataTestid: !this.isChina ? 'terms-conditions' : 'terms-and-policy'
      },
      {
        label: !this.isChina ? this.lblPrivacyPolicy : this.lblCrossTransferChina,
        rules: 'checked',
        name: 'customer.privacyPolicy',
        id: 'privacy-policy',
        checked: false,
        dataTestid: !this.isChina ? 'privacy-policy' : 'cross-transfer-china'
      },
      {
        label: this.lblMarketingEmailsConsent,
        name: 'customer.marketingEmailsConsent',
        id: 'marketing-emails',
        checked: false,
        dataTestid: 'marketing-emails'
      }
    ]
    if (this.consentPiplEnabled(global.AkamaiService.userCountry())) {
      this.checkboxes.push({
        label: this.lblBoCheckboxPIPL,
        rules: 'checked',
        name: 'customer.consentPIPL',
        id: 'checkboxPIPL',
        checked: false,
        dataTestid: 'checkboxPIPL'
      })
    }

    this.languages = await contentService.getLanguages()
    this.countries = await loyaltyService.getCountriesByMemberType('customer')
    this.getPromoCode()
    this.getRefererCode()
    this.getEmployeeCode()
    this.getCorporateInfo()
    this.$nextTick(() => {
      this.cssLoaded = true
    })
  }
}
</script>

<style lang="scss">
.field__error {
  row-gap: 0.1rem;
  color: crimson;
  font-size: 1.1rem;
}

.join-submit-btn {
  flex: auto;
  border-radius: 3rem;
  padding: 1.5rem 2.5rem !important;
}

.promo-code-message {
  font-size: 1.2rem;
}

.dynamic-checkbox {
  label.checkbox-field {
    font-size: 1.4rem;
    color: color('dark-grey');
    button.btn.btn-link {
      color: color('black');
    }
  }
}

.btn-primary {
  font-weight: 600;
}

#field-tel {
  .vti__dropdown {
    border-right: 1px solid lightgrey;
  }
  .vti__input {
    background-color: transparent;
  }
}

.field {
  &__input {
    background: transparent;
  }
  &__label {
    background: color('grey-soft') !important;
  }
}

.pass-show {
  font-weight: 300;
  @media (min-width: media('tablet')) {
    font-weight: 600;
  }
}

.join-submit-btn {
  width: 100%;
}

.join-corporate {
  .vti__country-code {
    color: #000 !important;
    font-size: 14px;
  }

  .field__label {
    background: #fff !important;
    color: #53565a;
  }

  .checkbox-field .checkbox-field__checkbox:checked ~ .checkbox-field__checkmark {
    background-color: #382246;
  }

  .join-submit-btn {
    background-color: #382246;
    width: auto;
    &:hover {
      background-color: #422d5a !important;
    }
    @media (max-width: media('tablet')) {
      width: 100%;
    }
  }

  .form__title {
    @media (max-width: media('tablet')) {
      font-weight: bolder;
    }
  }
}

.tooltip-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  margin-inline-start: 1rem !important;
}

.tooltip-text {
  position: absolute;
  bottom: 150%;
  left: 50%;
  transform: translateX(-50%);
  visibility: hidden;
  opacity: 0;
  z-index: 602;
  background: white;
  color: black;
  text-align: center;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 1.2rem;
  line-height: 1.4;
  width: max-content;
  max-width: 260px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transition: opacity 0.25s ease, visibility 0.25s ease;
}

.tooltip-text::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: white transparent transparent transparent;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.15));
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
</style>
